import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { SliderRow } from "@/components/countries/CardSlider";
import { testimonials } from "@/data/testimonials";

/* One story from each of twelve areas — the twelve with the most stories in
   the patient records — so the slider shows the range of what patients came
   for rather than many stories of one kind. Each area's first story is used. */
const AREAS = [
  "Panchakarma",
  "Joint & Spine",
  "Stress & Sleep",
  "Skin & Hair",
  "Weight Management",
  "Digestive",
  "Neuro",
  "Fertility & Women's Health",
  "Rejuvenation & Wellness",
  "Respiratory",
  "Immunity & Seasonal",
  "Sports & Injury Recovery",
];
const STORIES = AREAS.map((area) => testimonials.find((t) => t.category === area && t.image)).filter(
  (t): t is (typeof testimonials)[number] => !!t
);

/** the stories in a new random order (Fisher–Yates) */
function shuffled<T>(list: readonly T[]): T[] {
  const out = [...list];
  for (let i = out.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [out[i], out[j]] = [out[j], out[i]];
  }
  return out;
}

/** three across on a desktop, two on a tablet, one at a time on a phone */
const CARD_WIDTH = "w-full shrink-0 snap-start sm:w-[calc((100%-1rem)/2)] lg:w-[calc((100%-2rem)/3)]";

/**
 * Patient stories in a slider, as the country guides show them (the same
 * card and arrows), above the footer on every package, treatment and
 * holistic healing page (components/Footer). Each card opens the full story.
 * The order is shuffled each time a page shows it, and the arrows move a
 * whole view at a time: all three cards together on a desktop, one on a phone.
 */
export default function PatientStorySlider() {
  const [stories] = useState(() => shuffled(STORIES));
  if (!stories.length) return null;
  return (
    <section aria-labelledby="patient-story-slider" className="border-t border-border/60 bg-[#E5E7E2] py-12 md:py-14">
      <div className="container mx-auto max-w-6xl px-4">
        <h2 id="patient-story-slider" className="mb-1 text-xl font-bold text-[#2C4E5A] md:text-2xl">
          What International Patients Say
        </h2>
        <p className="mb-5 text-sm text-foreground">
          {stories.length} stories from our patient records, one from each area of treatment
        </p>
        <SliderRow label="patient stories" byPage>
          {stories.map((t) => (
            <Link
              key={t.slug}
              to={`/patient-testimonials/${t.slug}`}
              data-card
              className={`group flex flex-col overflow-hidden rounded-xl border border-border/60 bg-white transition-shadow hover:shadow-lg focus:outline-none focus-visible:ring-2 focus-visible:ring-primary ${CARD_WIDTH}`}
            >
              <img
                src={encodeURI(t.image!)}
                alt={t.imageAlt}
                loading="lazy"
                decoding="async"
                className="aspect-square w-full object-cover object-center"
              />
              <span className="flex flex-1 flex-col p-4">
                <span className="mb-1 self-start rounded-full bg-[#EDE8D0] px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-[#325A67]">
                  {t.category}
                </span>
                <span className="text-sm font-bold text-[#2C4E5A]">
                  {t.patientName}
                  {typeof t.age === "number" ? `, ${t.age}` : ""}
                </span>
                {t.treatment && <span className="mb-2 text-xs text-foreground">{t.treatment}</span>}
                {/* six lines, then an ellipsis, so the cards in a row stay level */}
                <span className="line-clamp-6 text-sm leading-relaxed">{t.shortTestimonial || t.excerpt}</span>
                <span className="mt-auto inline-flex items-center gap-1 self-end pt-3 text-xs font-bold uppercase tracking-tight text-[#C24E00]">
                  Read Full Story
                  <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5" />
                </span>
              </span>
            </Link>
          ))}
        </SliderRow>
        <div className="mt-5 flex justify-end">
          <Link
            to="/patient-testimonials"
            className="inline-flex items-center gap-1.5 text-base font-bold text-[#2C4E5A] transition-colors hover:text-[#C24E00] md:text-lg"
          >
            See all patient testimonials
            <ArrowRight className="h-5 w-5" />
          </Link>
        </div>
      </div>
    </section>
  );
}
